import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useDebounce } from '@/hooks';
import { DEBOUNCE_DELAY } from '@/constants';
import { Spinner } from './Spinner';

export interface SearchOption {
  id: number | string;
  name: string;
  subtitle?: string;
}

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => Promise<SearchOption[]>;
  onSelect?: (option: SearchOption) => void;
  error?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  minSearchLength?: number;
  displayField?: 'name' | ((option: SearchOption) => ReactNode);
}

export const SearchAutocomplete: FC<SearchAutocompleteProps> = ({
  value,
  onChange,
  onSearch,
  onSelect,
  error,
  required,
  label,
  placeholder = 'Start typing to search...',
  minSearchLength = 2,
  displayField = 'name',
}) => {
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [inputValue, setInputValue] = useState(value || '');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync inputValue with value prop when it changes externally
  useEffect(() => {
    if (value !== inputValue && value !== undefined) {
      setInputValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced search function
  const debouncedSearch = useDebounce(async (query: string) => {
    if (query && query.length >= minSearchLength) {
      setLoading(true);
      try {
        const results = await onSearch(query);
        setOptions(results);
      } catch (error) {
        console.error('Search failed:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    } else if (query.length < minSearchLength) {
      setOptions([]);
    }
  }, DEBOUNCE_DELAY);

  useEffect(() => {
    if (inputValue && inputValue !== value) {
      debouncedSearch(inputValue);
    } else {
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleSelect = (option: SearchOption) => {
    const selectedValue = typeof displayField === 'function' ? option.name : option[displayField] as string;
    onChange(selectedValue);
    if (onSelect) {
      onSelect(option);
    }
    setInputValue(selectedValue);
    setOptions([]);
    setIsFocused(false);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFocused]);

  const getDisplayValue = (option: SearchOption) => {
    if (typeof displayField === 'function') {
      return displayField(option);
    }
    return option[displayField];
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        </div>
      )}
      <div className="relative" ref={dropdownRef}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            // Don't clear the value on every keystroke - let user type freely
            // Only clear if they're typing something different from the selected value
            if (e.target.value !== value) {
              onChange('');
            }
          }}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          className="w-full h-9 px-3 text-sm text-slate-900 border border-slate-200 rounded-lg 
            focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-shadow"
        />
        {loading && (
          <div className="absolute right-3 top-2.5">
            <Spinner size="sm" />
          </div>
        )}
        {isFocused && options.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onMouseDown={(e) => {
                  // Prevent blur event from firing when clicking an option
                  e.preventDefault();
                  handleSelect(option);
                }}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {getDisplayValue(option)}
                {option.subtitle && (
                  <div className="text-xs text-slate-500">{option.subtitle}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

